// green-button.js
class GreenButton extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  static get observedAttributes() {
    return ['href', 'disabled', 'type', 'target'];
  }

  connectedCallback() {
    this.render();
    this.setupEventListeners();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  setupEventListeners() {
    const element = this.shadowRoot.querySelector('.green-button');
    
    if (!this.hasAttribute('href')) {
      element.addEventListener('click', (e) => {
        if (!this.disabled) {
          this.dispatchEvent(new CustomEvent('click', {
            bubbles: true,
            composed: true
          }));
        }
      });
    }

    // Prevent click when disabled
    element.addEventListener('click', (e) => {
      if (this.disabled) {
        e.preventDefault();
      }
    });
  }

  get disabled() {
    return this.hasAttribute('disabled');
  }

  set disabled(value) {
    if (value) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  render() {
    const style = `
      :host {
        display: inline-block;
      }

      .green-button {
        width: calc(148px - 4px);
        height: calc(48px - 4px);
        background: #31B261;
        border-radius: 24px;
        transition: border-color 0.3s linear;

        display: inline-flex;
        position: relative;
        align-items: center;
        justify-content: center;

        
        font-family: Metropolis, system-ui, -apple-system, sans-serif;
        line-height: 120%;
        font-size: 20px;
        font-weight: 600;
        color: white;
        text-decoration: none;
        user-select: none;
        cursor: pointer;
      }

      .green-button::before {
        content: "";
        display: block;
        position: absolute;
        inset: -2px;
        z-index: -1;
        background: inherit;
        border-radius: inherit;
      }

      .green-button:hover::before {
        background-image: linear-gradient(45deg, rgba(255, 255, 255, 40%), rgba(255, 255, 255, 60%));
      }

      .green-button:active::before {
        background-image: linear-gradient(45deg, rgba(255, 255, 255, 60%), rgba(255, 255, 255, 80%));
      }

      .green-button[disabled] {
        cursor: not-allowed;
        background-color: #a2a2a2;
      }

      /* Maintain consistent spacing when used as a button */
      button.green-button {
        line-height: normal;
        margin: 0;
      }

      @media screen and (orientation: portrait) and (max-width: 1081px) {
        .green-button {
          width: 132px;
          height: 40px;
          border-radius: 20px;
          font-size: 16px;
          line-height: 140%;
        }
      }
    `;

    const href = this.getAttribute('href');
    const disabled = this.hasAttribute('disabled');
    const type = this.getAttribute('type') || 'button';
    const target = this.getAttribute('target');

    let elementHtml;
    if (href && !disabled) {
      // Render as anchor
      elementHtml = `
        <a 
          class="green-button"
          href="${href}"
          ${target ? `target="${target}"` : ''}
          role="button"
        >
          <slot></slot>
        </a>
      `;
    } else {
      // Render as button
      elementHtml = `
        <button 
          class="green-button"
          type="${type}"
          ${disabled ? 'disabled' : ''}
        >
          <slot></slot>
        </button>
      `;
    }

    this.shadowRoot.innerHTML = `
      <style>${style}</style>
      ${elementHtml}
    `;
  }
}

customElements.define('green-button', GreenButton);